/**
 * Single source of truth for product billing + entry branding.
 * Add new products here first — Razorpay plan env keys, pricing model, and login routing stay in sync.
 */
import type { ProductId } from './products';

export const BILLING_CATALOG_VERSION = 1;

/** How checkout quantity is calculated for Razorpay subscriptions. */
export type ProductBillingModel = 'per_active_user' | 'per_org_flat' | 'per_assigned_user';

export interface ProductBillingDefinition {
  productId: ProductId;
  /** Env var suffix on backend (RAZORPAY_PLAN_{razorpayPlanEnvKey}) */
  razorpayPlanEnvKey: string;
  billingModel: ProductBillingModel;
  priceUsd: number;
  unitLabel: string;
  shortLabel: string;
  /** Always entitled for every org (Plan). */
  alwaysEntitled?: boolean;
  /** Org may subscribe without other products (future standalone SKUs). */
  standaloneAllowed?: boolean;
  /** Hostname prefix for product-branded login, e.g. code.cotaskr.com */
  loginHostPrefix?: string;
}

export const PRODUCT_BILLING: Record<ProductId, ProductBillingDefinition> = {
  pm: {
    productId: 'pm',
    razorpayPlanEnvKey: 'PM_MONTHLY',
    billingModel: 'per_active_user',
    priceUsd: 10,
    unitLabel: '/user/mo',
    shortLabel: 'Plan',
    alwaysEntitled: true,
    standaloneAllowed: true,
  },
  code: {
    productId: 'code',
    razorpayPlanEnvKey: 'CODE_MONTHLY',
    billingModel: 'per_assigned_user',
    priceUsd: 12,
    unitLabel: '/dev/mo',
    shortLabel: 'CodeFx',
    standaloneAllowed: true,
    loginHostPrefix: 'code',
  },
  ship: {
    productId: 'ship',
    razorpayPlanEnvKey: 'SHIP_MONTHLY',
    billingModel: 'per_assigned_user',
    priceUsd: 15,
    unitLabel: '/seat/mo',
    shortLabel: 'Ship',
    standaloneAllowed: true,
    loginHostPrefix: 'ship',
  },
  lenc: {
    productId: 'lenc',
    razorpayPlanEnvKey: 'LENC_MONTHLY',
    billingModel: 'per_org_flat',
    priceUsd: 29,
    unitLabel: '/org/mo',
    shortLabel: 'Lenc',
    standaloneAllowed: true,
    loginHostPrefix: 'lenc',
  },
};

export const BILLABLE_PRODUCT_IDS = Object.keys(PRODUCT_BILLING) as ProductId[];

export function isPerSeatProduct(id: ProductId): boolean {
  const model = PRODUCT_BILLING[id].billingModel;
  return model === 'per_active_user' || model === 'per_assigned_user';
}

export function resolveProductFromHostname(hostname: string): ProductId | null {
  const host = hostname.toLowerCase().split(':')[0];
  for (const def of Object.values(PRODUCT_BILLING)) {
    if (!def.loginHostPrefix) continue;
    if (host === def.loginHostPrefix || host.startsWith(`${def.loginHostPrefix}.`)) {
      return def.productId;
    }
  }
  return null;
}

export function seatQuantityForProduct(productId: ProductId, assignedCount: number): number {
  const def = PRODUCT_BILLING[productId];
  if (def.billingModel === 'per_org_flat') return assignedCount > 0 ? 1 : 0;
  if (productId === 'pm') return Math.max(1, assignedCount);
  return Math.max(0, assignedCount);
}

export function estimateProductMonthlyUsd(
  productId: ProductId,
  assignedCount: number,
): number {
  const def = PRODUCT_BILLING[productId];
  const qty = seatQuantityForProduct(productId, assignedCount);
  return def.priceUsd * qty;
}

export function estimateSelectedProductsMonthlyUsd(
  productIds: ProductId[],
  seatsByProduct: Partial<Record<ProductId, number>>,
): number {
  return productIds.reduce(
    (sum, id) => sum + estimateProductMonthlyUsd(id, seatsByProduct[id] ?? 0),
    0,
  );
}
